import { makeStyles } from '@material-ui/styles';
import { differenceInMinutes, format, parseISO } from 'date-fns';
import React from 'react';

import { getFullName } from 'src/common/formatting/getFullName';
import { Colors } from 'src/common/ui/base/Colors';
import { ExpandCollapseSection } from 'src/common/ui/components/ExpandCollapseSection';
import IconInBox from 'src/common/ui/components/IconInBox/IconInBox';
import type { UnsignedVisit } from 'src/myDayToday/types/acceptNextPatientCard';

type UnsignedVisitNotesSectionProps = {
  unsignedVisits: UnsignedVisit[];
  now: Date;
  onVisitClick: (visitId: string) => void;
};

function getPatientName(visit: UnsignedVisit): string {
  const patient = visit.attendeesDisplay.find(a => a?.userType === 'Patient') ?? null;
  return getFullName(patient) || 'Unknown patient';
}

function getStartLabel(visit: UnsignedVisit, now: Date): string | null {
  if (!visit.start) return null;
  const startTime = parseISO(visit.start);
  const minsAgo = differenceInMinutes(now, startTime);
  return `Visit started ${minsAgo} minute${minsAgo !== 1 ? 's' : ''} ago at ${format(
    startTime,
    'h:mm a',
  )}`;
}

function VisitNoteRow({
  visit,
  now,
  onVisitClick,
}: {
  visit: UnsignedVisit;
  now: Date;
  onVisitClick: (visitId: string) => void;
}) {
  const styles = useUnsignedNotesStyles();
  const patientName = getPatientName(visit);
  const startLabel = getStartLabel(visit, now);

  return (
    <button type="button" className={styles.noteRow} onClick={() => onVisitClick(visit.id)}>
      <span className={styles.noteTitle}>
        Sign and complete intake (new) note for {patientName}
      </span>
      {startLabel && <span className={styles.noteSubtitle}>{startLabel}</span>}
    </button>
  );
}

export function UnsignedVisitNotesSection({
  unsignedVisits,
  now,
  onVisitClick,
}: UnsignedVisitNotesSectionProps) {
  const styles = useUnsignedNotesStyles();

  const count = unsignedVisits.length;
  const label = `${count} UNSIGNED VISIT NOTE${count !== 1 ? 'S' : ''}`;

  return (
    <ExpandCollapseSection label={label}>
      {count === 0 ? (
        <div className={styles.emptyState}>
          <IconInBox>
            <IconInBox.Notes />
          </IconInBox>
          <p className={styles.emptyTitle}>You&apos;re all done with your visit notes for now!</p>
          <p className={styles.emptySubtext}>The world is your oyster.</p>
          <p className={styles.emptySubtext}>Go out there and get &apos;em tiger.</p>
        </div>
      ) : (
        unsignedVisits.map(visit => (
          <VisitNoteRow key={visit.id} visit={visit} now={now} onVisitClick={onVisitClick} />
        ))
      )}
    </ExpandCollapseSection>
  );
}

const useUnsignedNotesStyles = makeStyles({
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
  },
  emptyTitle: {
    margin: 0,
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 16,
    fontWeight: 600,
    color: Colors.CoolGray9,
    textAlign: 'center',
  },
  emptySubtext: {
    margin: 0,
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 14,
    fontWeight: 400,
    color: Colors.CoolGray9,
    textAlign: 'center',
  },
  noteRow: {
    background: Colors.White,
    border: `1px solid ${Colors.CoolGray2}`,
    borderRadius: 12,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    margin: '0 8px',
    padding: '8px 16px',
    '&:hover': {
      background: 'rgba(0,0,0,0.04)',
    },
  },
  noteTitle: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 16,
    fontWeight: 600,
    color: Colors.CoolGray9,
  },
  noteSubtitle: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 14,
    fontWeight: 600,
    color: Colors.CoolGray9,
  },
});
